import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";

// Create the UserRole context
const UserRoleContext = createContext();

// Define available roles with metadata
// Define new Icons
const PhoneArrowUpRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 4.5l-15 15m0 0h11.25m-11.25 0V8.25"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
    />
  </svg>
);
const CurrencyDollarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
    />
  </svg>
);

const AVAILABLE_ROLES = {
  fronter: {
    // Added Fronter
    id: "fronter",
    name: "Fronter",
    description: "Qualify leads & set appointments", // Shorter description
    icon: <PhoneArrowUpRightIcon />, // Use component
  },
  closer: {
    // Added Closer
    id: "closer",
    name: "Closer",
    description: "Manage deals & close sales", // Shorter description
    icon: <CurrencyDollarIcon />, // Use component
  },
  manager: {
    id: "manager",
    name: "Manager",
    description: "Oversee team performance and campaigns",
    icon: null,
  },
  admin: {
    id: "admin",
    name: "Admin",
    description: "Configure system settings and manage users",
    icon: null,
  },
  agent: {
    id: "agent",
    name: "Agent",
    description: "General agent role",
    icon: null,
  },
};
const VALID_ROLES = Object.keys(AVAILABLE_ROLES); // Get valid roles dynamically

/**
 * UserRoleProvider Component
 *
 * Provides role management functionality throughout the application
 * Supports dynamic UI adaptation based on user role (agent, manager, admin)
 * Persists role selection in local storage
 */
export const UserRoleProvider = ({ children }) => {
  // Get authenticated user and auth state - with fallback for when auth context isn't available
  // This makes it work in development/testing environments
  const auth = useAuth();
  const user = auth?.user;
  const loading = auth?.loading || false;

  // Derive role from the authenticated user object.
  // We intentionally do NOT persist role in localStorage because the API is the
  // source of truth — a stale cached value would override the real role on every
  // subsequent page load.
  // Normalize backend role strings to frontend VALID_ROLES.
  // Backend may send "system_admin" or "super_admin" for admin users.
  const normalizeRole = (raw) => {
    if (!raw) return null;
    if (VALID_ROLES.includes(raw)) return raw;
    if (raw === "system_admin" || raw === "super_admin") return "admin";
    return null;
  };

  const getRoleFromUser = (u) => {
    if (!u) return "fronter";
    const fromRole = normalizeRole(u.role);
    if (fromRole) return fromRole;
    if (Array.isArray(u.roles) && u.roles.length > 0) {
      const fromRoles = normalizeRole(u.roles[0]);
      if (fromRoles) return fromRoles;
    }
    return "fronter";
  };

  // Derive the role during render, not in an effect: route guards read it in
  // the same render that the session probe resolves, and a one-render lag sent
  // every deep link to a role-gated route back to /dashboard.
  const [roleOverride, setRoleOverride] = useState(null);
  const role = roleOverride ?? getRoleFromUser(user);

  // Clear any stale localStorage role override on mount (API is source of truth)
  useEffect(() => {
    localStorage.removeItem("userRole");
  }, []);

  // A new session (login, me-refresh, logout) drops any in-memory override.
  useEffect(() => {
    setRoleOverride(null);
  }, [user]);

  // Get all available roles
  const getAvailableRoles = () => {
    // In production, filter based on user's actual roles if available
    if (user?.roles && Array.isArray(user.roles)) {
      return Object.values(AVAILABLE_ROLES).filter((role) =>
        user.roles.includes(role.id),
      );
    }

    // If user has single role property, return just that role
    if (user?.role && VALID_ROLES.includes(user.role)) {
      return [AVAILABLE_ROLES[user.role]];
    }

    // Fallback: return all roles
    return Object.values(AVAILABLE_ROLES);
  };

  // Get current role details
  const getCurrentRoleDetails = () => {
    return AVAILABLE_ROLES[role] || AVAILABLE_ROLES.fronter; // Default details to fronter
  };

  // Access permissions for different roles
  // Define permissions based on the new roles
  const rolePermissions = {
    fronter: {
      canCall: true,
      canRequestBarge: true, // Specific fronter action
      canSetAppointment: true,
      canViewOwnStats: true,
      canViewCampaigns: true, // View assigned campaigns
      canCreateCampaigns: false,
      canViewAllAgents: false,
      canAccessSettings: false,
      canManageUsers: false,
      canManageDeals: false,
      canMonitorCalls: false,
    },
    closer: {
      canCall: true,
      canAcceptBarge: true, // Specific closer action
      canManageDeals: true,
      canViewOwnStats: true,
      canViewCampaigns: true,
      canCreateCampaigns: false,
      canViewAllAgents: false,
      canAccessSettings: false,
      canManageUsers: false,
      canMonitorCalls: false,
    },
    manager: {
      canCall: true,
      canAcceptBarge: true, // Managers might also barge
      canManageDeals: true, // View team deals
      canViewOwnStats: true,
      canViewTeamStats: true,
      canViewCampaigns: true,
      canCreateCampaigns: true,
      canViewAllAgents: true,
      canAccessSettings: true, // Limited settings?
      canManageUsers: false, // Maybe assign agents to campaigns?
      canMonitorCalls: true, // Listen, Whisper, Barge
    },
    admin: {
      canCall: true, // Admins can likely do everything
      canAcceptBarge: true,
      canManageDeals: true,
      canViewOwnStats: true,
      canViewTeamStats: true,
      canViewAllStats: true,
      canViewCampaigns: true,
      canCreateCampaigns: true,
      canViewAllAgents: true,
      canAccessSettings: true, // Full settings
      canManageUsers: true, // Full user management
      canMonitorCalls: true,
    },
    // Permissions for legacy 'agent' role if kept
    agent: {
      canCall: true,
      canCreateCampaigns: false,
      canViewAllAgents: false,
      canAccessSettings: false,
      canManageUsers: false,
      canManageDeals: false,
      canMonitorCalls: false,
    },
  };

  // Update role in memory only (no localStorage — API role is source of truth)
  const updateRole = (newRole) => {
    if (VALID_ROLES.includes(newRole)) {
      setRoleOverride(newRole);
    }
  };

  // Check if user has a specific permission based on their role
  const hasPermission = (permission) => {
    return rolePermissions[role]?.[permission] || false;
  };

  return (
    <UserRoleContext.Provider
      value={{
        role,
        setRole: updateRole, // Keep setRole alias for compatibility if needed
        updateRole,
        hasPermission,
        permissions: rolePermissions[role] || {}, // Provide current permissions object
        getAvailableRoles,
        getCurrentRoleDetails,
        allRoles: AVAILABLE_ROLES,
        validRoles: VALID_ROLES,
      }}
    >
      {children}
    </UserRoleContext.Provider>
  );
};

// Custom hook for using the UserRole context
export const useUserRole = () => {
  const context = useContext(UserRoleContext);

  if (!context) {
    throw new Error("useUserRole must be used within a UserRoleProvider");
  }

  return context;
};

// Default export removed as Provider and hook are named exports
